import type { RouteRecordRaw } from 'vue-router'

const serviceBridgeListPermissions = [
  'service-bridge:database:account:list',
  'service-bridge:database:binding:list',
  'service-bridge:database:usage:list',
  'service-bridge:proxy:node:list',
  'service-bridge:proxy:grant:list',
  'service-bridge:proxy:usage:list',
  'service-bridge:collection:list',
  'service-bridge:collection:task:list',
  'service-bridge:customer:list',
  'service-bridge:wecom:customer:list',
  'service-bridge:wecom:application:list',
  'service-bridge:wecom:provider:list',
  'service-bridge:wecom:template:list',
  'service-bridge:wecom:callback-log:list',
  'service-bridge:wecom:callback-subscription:list',
  'service-bridge:wecom:callback-delivery-log:list',
  'service-bridge:wechat-mini-program:list',
  'service-bridge:wechat-official-account:account:list',
  'service-bridge:wechat-official-account:binding:list',
  'service-bridge:wechat-official-account:template:list',
  'service-bridge:wechat-official-account:grant:list',
  'service-bridge:cos:account:list',
  'service-bridge:cos:binding:list',
  'service-bridge:cos:usage:list',
  'service-bridge:onepanel:list',
  'service-bridge:ai-employee:list',
  'service-bridge:payment:merchant:list',
  'service-bridge:payment:wallet:list',
  'service-bridge:payment:transaction:list',
  'service-bridge:payment:order:list',
  'service-bridge:payment:ai-recharge:list',
  'service-bridge:payment:refund:list',
  'service-bridge:payment:reconciliation:list',
]

const wecomListPermissions = [
  ...serviceBridgeListPermissions.filter(permission =>
    permission.startsWith('service-bridge:wecom:'),
  ),
]

const cosListPermissions = serviceBridgeListPermissions.filter(permission =>
  permission.startsWith('service-bridge:cos:'),
)

const routes: RouteRecordRaw = {
  path: 'service-bridge',
  name: 'ControlCenterServiceBridge',
  meta: {
    title: 'ServiceBridge',
    icon: 'i-ant-design:partition-outlined',
    auth: serviceBridgeListPermissions,
  },
  children: [
    {
      path: 'database',
      name: 'ServiceBridgeDatabase',
      meta: {
        title: '数据库统计',
        icon: 'i-lucide:database',
        auth: ['service-bridge:database:account:list', 'service-bridge:database:binding:list', 'service-bridge:database:usage:list'],
      },
      children: [
        {
          path: 'accounts',
          name: 'ServiceBridgeDatabaseAccounts',
          component: () => import('@/views/third-party-integration/database/index.vue'),
          props: { section: 'accounts' },
          meta: { title: '采集连接', icon: 'i-lucide:server', auth: 'service-bridge:database:account:list' },
        },
        {
          path: 'bindings',
          name: 'ServiceBridgeDatabaseBindings',
          component: () => import('@/views/third-party-integration/database/index.vue'),
          props: { section: 'bindings' },
          meta: { title: '客户数据库', icon: 'i-lucide:shield-check', auth: 'service-bridge:database:binding:list' },
        },
        {
          path: 'usage',
          name: 'ServiceBridgeDatabaseUsage',
          component: () => import('@/views/third-party-integration/database/index.vue'),
          props: { section: 'usage' },
          meta: { title: '用量统计', icon: 'i-lucide:chart-no-axes-combined', auth: 'service-bridge:database:usage:list' },
        },
      ],
    },
    {
      path: 'proxy',
      name: 'ServiceBridgeProxy',
      meta: {
        title: '代理网关',
        icon: 'i-lucide:network',
        auth: ['service-bridge:proxy:node:list', 'service-bridge:proxy:grant:list', 'service-bridge:proxy:usage:list'],
      },
      children: [
        {
          path: 'nodes',
          name: 'ServiceBridgeProxyNodes',
          component: () => import('@/views/third-party-integration/proxy/index.vue'),
          props: { section: 'nodes' },
          meta: { title: '代理节点', icon: 'i-lucide:server', auth: 'service-bridge:proxy:node:list' },
        },
        {
          path: 'grants',
          name: 'ServiceBridgeProxyGrants',
          component: () => import('@/views/third-party-integration/proxy/index.vue'),
          props: { section: 'grants' },
          meta: { title: '模块授权', icon: 'i-lucide:shield-check', auth: 'service-bridge:proxy:grant:list' },
        },
        {
          path: 'usage',
          name: 'ServiceBridgeProxyUsage',
          component: () => import('@/views/third-party-integration/proxy/index.vue'),
          props: { section: 'usage' },
          meta: { title: '用量与记录', icon: 'i-lucide:chart-no-axes-combined', auth: 'service-bridge:proxy:usage:list' },
        },
      ],
    },
    {
      path: 'collection',
      name: 'ServiceBridgeCollection',
      meta: {
        title: '信息采集',
        icon: 'i-lucide:database-zap',
        auth: ['service-bridge:collection:list', 'service-bridge:collection:task:list'],
      },
      children: [
        {
          path: 'services',
          name: 'ServiceBridgeCollectionServices',
          component: () => import('@/views/third-party-integration/collection/index.vue'),
          props: { section: 'services' },
          meta: {
            title: '服务与定价',
            icon: 'i-lucide:tags',
            auth: 'service-bridge:collection:list',
            activeMenu: '/control-center/service-bridge/collection/services',
          },
        },
        {
          path: 'accounts',
          name: 'ServiceBridgeCollectionAccounts',
          component: () => import('@/views/third-party-integration/collection/index.vue'),
          props: { section: 'accounts' },
          meta: {
            title: '供应商账号',
            icon: 'i-lucide:key-round',
            auth: 'service-bridge:collection:list',
            activeMenu: '/control-center/service-bridge/collection/accounts',
          },
        },
        {
          path: 'grants',
          name: 'ServiceBridgeCollectionGrants',
          component: () => import('@/views/third-party-integration/collection/index.vue'),
          props: { section: 'grants' },
          meta: {
            title: '客户授权',
            icon: 'i-lucide:shield-check',
            auth: 'service-bridge:collection:list',
            activeMenu: '/control-center/service-bridge/collection/grants',
          },
        },
        {
          path: 'tasks',
          name: 'ServiceBridgeCollectionTasks',
          component: () => import('@/views/third-party-integration/collection/index.vue'),
          props: { section: 'tasks' },
          meta: {
            title: '任务记录',
            icon: 'i-lucide:clipboard-list',
            auth: 'service-bridge:collection:task:list',
            activeMenu: '/control-center/service-bridge/collection/tasks',
          },
        },
      ],
    },
    {
      path: 'customer',
      name: 'ServiceBridgeCustomer',
      component: () => import('@/views/third-party-integration/wecom/customer/index.vue'),
      meta: {
        title: '业务客户',
        icon: 'i-ant-design:team-outlined',
        auth: 'service-bridge:customer:list',
        activeMenu: '/control-center/service-bridge/customer',
      },
    },
    {
      path: 'wecom',
      name: 'ControlCenterServiceBridgeWecom',
      meta: {
        title: '企业微信',
        icon: 'i-ri:wechat-line',
        auth: wecomListPermissions,
      },
      children: [
        {
          path: 'customer',
          alias: [
            '/control-center/wecom/customer',
            '/third-party-integration/wecom/customer',
          ],
          name: 'ServiceBridgeWecomCustomer',
          component: () => import('@/views/third-party-integration/wecom/customer-management/index.vue'),
          meta: {
            title: '客户管理',
            icon: 'i-ant-design:team-outlined',
            auth: 'service-bridge:wecom:customer:list',
            activeMenu: '/control-center/service-bridge/wecom/customer',
          },
        },
        {
          path: 'application',
          alias: [
            '/control-center/wecom/application',
            '/third-party-integration/wecom/application',
          ],
          name: 'ServiceBridgeWecomApplication',
          component: () => import('@/views/third-party-integration/wecom/application/index.vue'),
          meta: {
            title: '应用列表',
            icon: 'i-ant-design:deployment-unit-outlined',
            auth: 'service-bridge:wecom:application:list',
            activeMenu: '/control-center/service-bridge/wecom/application',
          },
        },
        {
          path: 'provider',
          alias: [
            '/control-center/wecom/provider',
            '/third-party-integration/wecom/provider',
          ],
          name: 'ServiceBridgeWecomProvider',
          component: () => import('@/views/third-party-integration/wecom/provider/index.vue'),
          meta: {
            title: '服务商信息',
            icon: 'i-ant-design:appstore-outlined',
            auth: 'service-bridge:wecom:provider:list',
            activeMenu: '/control-center/service-bridge/wecom/provider',
          },
        },
        {
          path: 'template',
          alias: [
            '/control-center/wecom/template',
            '/third-party-integration/wecom/template',
          ],
          name: 'ServiceBridgeWecomTemplate',
          component: () => import('@/views/third-party-integration/wecom/template/index.vue'),
          meta: {
            title: 'Suite/模板',
            icon: 'i-ant-design:profile-outlined',
            auth: 'service-bridge:wecom:template:list',
            activeMenu: '/control-center/service-bridge/wecom/template',
          },
        },
        {
          path: 'contact',
          alias: [
            '/control-center/wecom/contact',
            '/third-party-integration/wecom/contact',
          ],
          name: 'ServiceBridgeWecomContact',
          component: () => import('@/views/third-party-integration/wecom/contact/index.vue'),
          meta: {
            title: '通讯录授权',
            icon: 'i-ant-design:contacts-outlined',
            auth: [
              'service-bridge:wecom:template:list',
              'service-bridge:wecom:customer:list',
            ],
            activeMenu: '/control-center/service-bridge/wecom/contact',
          },
        },
        {
          path: 'callback-subscription',
          alias: [
            '/control-center/wecom/callback-subscription',
            '/third-party-integration/wecom/callback-subscription',
          ],
          name: 'ServiceBridgeWecomCallbackSubscription',
          component: () => import('@/views/third-party-integration/wecom/callback-subscription/index.vue'),
          meta: {
            title: '回调订阅',
            icon: 'i-ant-design:notification-outlined',
            auth: 'service-bridge:wecom:callback-subscription:list',
            activeMenu: '/control-center/service-bridge/wecom/callback-subscription',
          },
        },
        {
          path: 'callback-log',
          alias: [
            '/control-center/wecom/callback-log',
            '/third-party-integration/wecom/callback-log',
          ],
          name: 'ServiceBridgeWecomCallbackLog',
          component: () => import('@/views/third-party-integration/wecom/callback-log/index.vue'),
          meta: {
            title: '回调日志',
            icon: 'i-ant-design:file-text-outlined',
            auth: 'service-bridge:wecom:callback-log:list',
            activeMenu: '/control-center/service-bridge/wecom/callback-log',
          },
        },
        {
          path: 'callback-delivery-log',
          alias: [
            '/control-center/wecom/callback-delivery-log',
            '/third-party-integration/wecom/callback-delivery-log',
          ],
          name: 'ServiceBridgeWecomCallbackDeliveryLog',
          component: () => import('@/views/third-party-integration/wecom/callback-delivery-log/index.vue'),
          meta: {
            title: '投递日志',
            icon: 'i-ant-design:send-outlined',
            auth: 'service-bridge:wecom:callback-delivery-log:list',
            activeMenu: '/control-center/service-bridge/wecom/callback-delivery-log',
          },
        },
      ],
    },
    {
      path: 'mini-program',
      name: 'ControlCenterServiceBridgeWechatMiniProgram',
      meta: {
        title: '小程序',
        icon: 'i-ri:mini-program-line',
        auth: 'service-bridge:wechat-mini-program:list',
      },
      children: [
        {
          path: 'manage',
          alias: [
            '/control-center/wecom/mini-program',
            '/third-party-integration/wecom/mini-program',
          ],
          name: 'ServiceBridgeWechatMiniProgram',
          component: () => import('@/views/third-party-integration/wechat-mini-program/index.vue'),
          meta: {
            title: '小程序管理',
            icon: 'i-ri:mini-program-line',
            auth: 'service-bridge:wechat-mini-program:list',
            activeMenu: '/control-center/service-bridge/mini-program/manage',
          },
        },
      ],
    },
    {
      path: 'wechat-official-account',
      name: 'ServiceBridgeWechatOfficialAccount',
      component: () => import('@/views/third-party-integration/wechat-official-account/index.vue'),
      meta: {
        title: '微信服务号',
        icon: 'i-ri:wechat-2-line',
        auth: serviceBridgeListPermissions.filter(permission => permission.startsWith('service-bridge:wechat-official-account:')),
        activeMenu: '/control-center/service-bridge/wechat-official-account',
      },
    },
    {
      path: 'cos',
      name: 'ControlCenterServiceBridgeCos',
      meta: { title: 'COS统计', icon: 'i-lucide:cloud', auth: cosListPermissions },
      children: [
        {
          path: 'accounts',
          alias: 'buckets',
          name: 'ServiceBridgeCosAccounts',
          component: () => import('@/views/third-party-integration/cos-statistics/index.vue'),
          props: { section: 'accounts' },
          meta: { title: '采集连接', icon: 'i-lucide:server', auth: 'service-bridge:cos:account:list' },
        },
        {
          path: 'bindings',
          alias: ['projects', 'grants'],
          name: 'ServiceBridgeCosBindings',
          component: () => import('@/views/third-party-integration/cos-statistics/index.vue'),
          props: { section: 'bindings' },
          meta: { title: '客户存储', icon: 'i-lucide:folder', auth: 'service-bridge:cos:binding:list' },
        },
        {
          path: 'usage',
          alias: 'objects',
          name: 'ServiceBridgeCosUsage',
          component: () => import('@/views/third-party-integration/cos-statistics/index.vue'),
          props: { section: 'usage' },
          meta: { title: '用量统计', icon: 'i-lucide:chart-line', auth: 'service-bridge:cos:usage:list' },
        },
      ],
    },
    {
      path: 'ai-employees',
      name: 'ServiceBridgeAiEmployees',
      component: () => import('@/views/third-party-integration/ai-employee/index.vue'),
      meta: {
        title: 'AI员工',
        icon: 'i-ant-design:robot-outlined',
        auth: 'service-bridge:ai-employee:list',
        activeMenu: '/control-center/service-bridge/ai-employees',
      },
    },
    {
      path: 'payment',
      name: 'ServiceBridgePayment',
      component: () => import('@/views/third-party-integration/payment/index.vue'),
      meta: {
        title: '微信支付',
        icon: 'i-ri:wechat-pay-line',
        auth: serviceBridgeListPermissions.filter(permission => permission.startsWith('service-bridge:payment:')),
        activeMenu: '/control-center/service-bridge/payment',
      },
    },
    {
      path: 'onepanel',
      name: 'ControlCenterServiceBridgeOnePanel',
      meta: {
        title: 'OnePanel',
        icon: 'i-ant-design:control-outlined',
        auth: 'service-bridge:onepanel:list',
      },
      children: [
        {
          path: 'instances',
          name: 'ServiceBridgeOnePanelInstances',
          component: () => import('@/views/third-party-integration/onepanel/instance/index.vue'),
          meta: {
            title: '面板实例',
            icon: 'i-ant-design:cloud-server-outlined',
            auth: 'service-bridge:onepanel:list',
            activeMenu: '/control-center/service-bridge/onepanel/instances',
          },
        },
        {
          path: 'capabilities',
          name: 'ServiceBridgeOnePanelCapabilities',
          component: () => import('@/views/third-party-integration/onepanel/capability/index.vue'),
          meta: {
            title: '能力清单',
            icon: 'i-ant-design:api-outlined',
            auth: 'service-bridge:onepanel:list',
            activeMenu: '/control-center/service-bridge/onepanel/capabilities',
          },
        },
        {
          path: 'resources',
          name: 'ServiceBridgeOnePanelResources',
          component: () => import('@/views/third-party-integration/onepanel/resource/index.vue'),
          meta: {
            title: '公共资源',
            icon: 'i-ant-design:database-outlined',
            auth: 'service-bridge:onepanel:list',
            activeMenu: '/control-center/service-bridge/onepanel/resources',
          },
        },
        {
          path: 'grants',
          name: 'ServiceBridgeOnePanelGrants',
          component: () => import('@/views/third-party-integration/onepanel/grant/index.vue'),
          meta: {
            title: '客户授权',
            icon: 'i-ant-design:safety-certificate-outlined',
            auth: 'service-bridge:onepanel:list',
            activeMenu: '/control-center/service-bridge/onepanel/grants',
          },
        },
      ],
    },
  ],
}

export default routes
